import {
  getPossibleVariables,
  resolveInputWithVars,
  type GetVariables,
  type Block,
  type OgImageBlock,
} from '@basehub/data/replicache/index'
import { ImageIcon, SquareIcon, TextIcon } from '@radix-ui/react-icons'
import { Button } from '~/sotano-kit/primitives/button'
import {
  blogArticleTemplateLayer,
  docsTemplateLayer,
  getOgBlockStore,
  newImageLayer,
  newShapeLayer,
  newTextLayer,
  useLayerFunctions,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/og-image/utils'
import {
  OgImageBlockImageLayerType,
  OgImageBlockLayerType,
  OgImageBlockShapeLayerType,
  OgImageBlockTextLayerType,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/og-image/types'
import * as React from 'react'
import { useEditorMode } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/editor-mode-provider'
import { isHotkey } from 'is-hotkey'
import { OgImageLayerContextMenuWrapper } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/og-image/context-menu'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { getOgBlockRootOptions } from './vars'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useEditorStore } from '../../editor-store'
import { useRepoStore } from '../../repo-store'
import { Icon } from 'basehub/react-icon'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useTreeSlice } from '~/hooks/use-tree-slice'

export const OG_IMAGE_WIDTH = 1200
export const OG_IMAGE_HEIGHT = 630

export const OgImageBlockInput = ({
  block: _block,
  parent,
  path,
  readOnly,
}: {
  block: OgImageBlock
  parent: Block
  path: string
  readOnly?: boolean
}) => {
  const editorMode = useEditorMode()
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const repo = useSelectedTeamAndRepo().data?.repo
  const sourceBlockId = idToSourceBlockId(_block)
  const setFocus = useRepoStore((s) => s.setFocus)

  const selectedLayerPositionZ = getOgBlockStore(_block.id)(
    (state) => state.selectedLayerPositionZ
  )
  const floatingLabelTwitter = getOgBlockStore(_block.id)(
    (state) => state.floatingLabelTwitter
  )

  const layerFunctions = useLayerFunctions({
    block: _block,
    path,
    setSelectedBlockId,
    parent,
  })

  const {
    value,
    addLayer,
    updateLayer,
    selectLayer,
    deselectLayer,
    removeLayer,
    setShowTemplates,
    addTemplate,
    reset,
  } = layerFunctions

  const isBlockSelected = useEditorStore(
    (s) => s.selectedBlockId === sourceBlockId
  )
  const isReadOnly = readOnly || editorMode === 'read'
  const overriding = !isReadOnly && parent.type === 'instance'
  const hasOverrides = Object.keys(_block.value?.layersData ?? {}).length > 0
  const [isDragging, setIsDragging] = React.useState(false)

  // Deselect layer when block is deselected
  React.useEffect(() => {
    if (!isBlockSelected) {
      deselectLayer()
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlockSelected])

  // Handlers
  async function handleSelectedLayerKeyDown(
    e: React.KeyboardEvent<HTMLButtonElement>,
    layer: OgImageBlockLayerType
  ) {
    if (layer.positionZ.toString() === selectedLayerPositionZ?.toString()) {
      if (isHotkey(['delete', 'backspace'], e)) {
        e.preventDefault()
        e.stopPropagation()

        await removeLayer(layer)
      }
      if (isHotkey('ArrowUp', e)) {
        e.preventDefault()
        e.stopPropagation()

        await updateLayer({
          ...layer,
          positionY: layer.positionY - 1,
        })
      }
      if (isHotkey('shift+ArrowUp', e)) {
        e.preventDefault()
        e.stopPropagation()

        await updateLayer({
          ...layer,
          positionY: layer.positionY - 10,
        })
      }
      if (isHotkey('ArrowDown', e)) {
        e.preventDefault()
        e.stopPropagation()

        await updateLayer({
          ...layer,
          positionY: layer.positionY + 1,
        })
      }
      if (isHotkey('shift+ArrowDown', e)) {
        e.preventDefault()
        e.stopPropagation()

        await updateLayer({
          ...layer,
          positionY: layer.positionY + 10,
        })
      }
      if (isHotkey('ArrowLeft', e)) {
        e.preventDefault()
        e.stopPropagation()

        await updateLayer({
          ...layer,
          positionX: layer.positionX - 1,
        })
      }
      if (isHotkey('shift+ArrowLeft', e)) {
        e.preventDefault()
        e.stopPropagation()

        await updateLayer({
          ...layer,
          positionX: layer.positionX - 10,
        })
      }
      if (isHotkey('ArrowRight', e)) {
        e.preventDefault()
        e.stopPropagation()

        await updateLayer({
          ...layer,
          positionX: layer.positionX + 1,
        })
      }
      if (isHotkey('shift+ArrowRight', e)) {
        e.preventDefault()
        e.stopPropagation()

        await updateLayer({
          ...layer,
          positionX: layer.positionX + 10,
        })
      }

      if (isHotkey('meta+x', e)) {
        e.preventDefault()
        e.stopPropagation()
      }

      if (isHotkey('meta+c', e)) {
        e.preventDefault()
        e.stopPropagation()
      }

      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        setFocus({ type: 'block:wrapper', blockId: sourceBlockId })
        await deselectLayer()
      }
    }
  }

  async function handleSelectedLayerMouseDown(
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType
  ) {
    if (!isReadOnly) {
      e.preventDefault()
      e.stopPropagation()
      // Select layer
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      e.currentTarget.focus()
      selectLayer(layer)
      setIsDragging(true)

      // Move layer
      const startX = e.clientX
      const startY = e.clientY
      const startLayerX = layer.positionX
      const startLayerY = layer.positionY

      const onMouseMove = (e: MouseEvent) => {
        const scaleFactor = relativeContainerWidth / OG_IMAGE_WIDTH

        const diffX = Math.round((e.clientX - startX) / scaleFactor)
        const diffY = Math.round((e.clientY - startY) / scaleFactor)

        const newX = startLayerX + diffX
        const newY = startLayerY + diffY

        const maxX = OG_IMAGE_WIDTH - layer.width
        const maxY = OG_IMAGE_HEIGHT - layer.height

        updateLayer({
          ...layer,
          positionX: newX < 0 ? 0 : newX > maxX ? maxX : newX,
          positionY: newY < 0 ? 0 : newY > maxY ? maxY : newY,
        })
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        setIsDragging(false)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }
  }

  async function handleCornerControlMouseDown(
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 'tl' | 'tr' | 'bl' | 'br'
  ) {
    if (!isReadOnly) {
      e.preventDefault()
      e.stopPropagation()
      // Select layer
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      e.currentTarget.focus()
      selectLayer(layer)

      // Resize layer
      const startX = e.clientX
      const startY = e.clientY
      const startLayerX = layer.positionX
      const startLayerY = layer.positionY
      const startLayerWidth = layer.width
      const startLayerHeight = layer.height

      const onMouseMove = async (e: MouseEvent) => {
        const scaleFactor = relativeContainerWidth / OG_IMAGE_WIDTH

        const diffX = Math.round((e.clientX - startX) / scaleFactor)
        const diffY = Math.round((e.clientY - startY) / scaleFactor)

        let newX = startLayerX
        let newY = startLayerY
        let newWidth = startLayerWidth
        let newHeight = startLayerHeight

        if (control === 'tl' || control === 'bl') {
          newX = startLayerX + diffX
          newWidth = startLayerWidth - diffX

          // Ensure the new width does not exceed the container's width
          if (newX < 0) {
            newWidth = startLayerWidth + startLayerX
            newX = 0
          }

          // Prevent the layer from moving after reaching its minimum width
          if (newWidth <= 0) {
            newWidth = 1
            newX = startLayerX + startLayerWidth - 1
          }
        }

        if (control === 'tl' || control === 'tr') {
          newY = startLayerY + diffY
          newHeight = startLayerHeight - diffY

          // Ensure the new height does not exceed the container's height
          if (newY < 0) {
            newHeight = startLayerHeight + startLayerY
            newY = 0
          }

          // Prevent the layer from moving after reaching its minimum height
          if (newHeight <= 0) {
            newHeight = 1
            newY = startLayerY + startLayerHeight - 1
          }
        }

        if (control === 'tr' || control === 'br') {
          newWidth = startLayerWidth + diffX

          // Ensure the new width does not exceed the container's width
          if (newX + newWidth > OG_IMAGE_WIDTH) {
            newWidth = OG_IMAGE_WIDTH - newX
          }
        }

        if (control === 'bl' || control === 'br') {
          newHeight = startLayerHeight + diffY

          // Ensure the new height does not exceed the container's height
          if (newY + newHeight > OG_IMAGE_HEIGHT) {
            newHeight = OG_IMAGE_HEIGHT - newY
          }
        }

        const maxX = OG_IMAGE_WIDTH - newWidth
        const maxY = OG_IMAGE_HEIGHT - newHeight

        updateLayer({
          ...layer,
          positionX: newX < 0 ? 0 : newX > maxX ? maxX : newX,
          positionY: newY < 0 ? 0 : newY > maxY ? maxY : newY,
          width:
            newWidth <= 0
              ? 1
              : newWidth > OG_IMAGE_WIDTH
                ? OG_IMAGE_WIDTH
                : newWidth,
          height:
            newHeight <= 0
              ? 1
              : newHeight > OG_IMAGE_HEIGHT
                ? OG_IMAGE_HEIGHT
                : newHeight,
        })
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }
  }

  async function handleSideControlMouseDown(
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 't' | 'r' | 'b' | 'l'
  ) {
    if (!isReadOnly) {
      e.preventDefault()
      e.stopPropagation()
      // Select layer
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      e.currentTarget.focus()
      selectLayer(layer)

      // Resize layer
      const startX = e.clientX
      const startY = e.clientY
      const startLayerX = layer.positionX
      const startLayerY = layer.positionY
      const startLayerWidth = layer.width
      const startLayerHeight = layer.height

      const onMouseMove = async (e: MouseEvent) => {
        const scaleFactor = relativeContainerWidth / OG_IMAGE_WIDTH

        const diffX = Math.round((e.clientX - startX) / scaleFactor)
        const diffY = Math.round((e.clientY - startY) / scaleFactor)

        let newX = startLayerX
        let newY = startLayerY
        let newWidth = startLayerWidth
        let newHeight = startLayerHeight

        if (control === 't') {
          newY = startLayerY + diffY
          newHeight = startLayerHeight - diffY
          // Ensure the new height does not exceed the container's height
          if (newY < 0) {
            newHeight = startLayerHeight + startLayerY
            newY = 0
          }

          // Prevent the layer from moving after reaching its minimum height
          if (newHeight <= 0) {
            newHeight = 1
            newY = startLayerY + startLayerHeight - 1
          }
        }
        if (control === 'r') {
          newWidth = startLayerWidth + diffX
          // Ensure the new width does not exceed the container's width
          if (newX + newWidth > OG_IMAGE_WIDTH) {
            newWidth = OG_IMAGE_WIDTH - newX
          }
        }
        if (control === 'b') {
          newHeight = startLayerHeight + diffY
          // Ensure the new height does not exceed the container's height
          if (newY + newHeight > OG_IMAGE_HEIGHT) {
            newHeight = OG_IMAGE_HEIGHT - newY
          }
        }
        if (control === 'l') {
          newX = startLayerX + diffX
          newWidth = startLayerWidth - diffX

          // Ensure the new width does not exceed the container's width
          if (newX < 0) {
            newWidth = startLayerWidth + startLayerX
            newX = 0
          }
          // Prevent the layer from moving after reaching its minimum width
          if (newWidth <= 0) {
            newWidth = 1
            newX = startLayerX + startLayerWidth - 1
          }
        }

        const maxX = OG_IMAGE_WIDTH - newWidth
        const maxY = OG_IMAGE_HEIGHT - newHeight

        updateLayer({
          ...layer,
          positionX: newX < 0 ? 0 : newX > maxX ? maxX : newX,
          positionY: newY < 0 ? 0 : newY > maxY ? maxY : newY,
          width:
            newWidth <= 0
              ? 1
              : newWidth > OG_IMAGE_WIDTH
                ? OG_IMAGE_WIDTH
                : newWidth,
          height:
            newHeight <= 0
              ? 1
              : newHeight > OG_IMAGE_HEIGHT
                ? OG_IMAGE_HEIGHT
                : newHeight,
        })
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }
  }

  // Load all fonts used in the text layers
  const fontsCSS = React.useMemo(() => {
    const usedFonts = value.layersData
      .filter((layer) => layer.type === 'text')
      .map((layer) => {
        if (layer.type === 'text') {
          return {
            fontFamily: layer.fontFamily,
            fontUrl: layer.fontUrl,
            fontWeight: layer.fontWeight,
          }
        }
      })
    // Remove duplicates
    const uniqueFonts = [...new Set(usedFonts)]

    return `
          ${uniqueFonts
            .map(
              (font) => `
            @font-face {
              font-family: '${font?.fontFamily}';
              src: url('${font?.fontUrl}') format('truetype');
              font-weight: ${font?.fontWeight};
              fallback: block;
            }
          `
            )
            .join('')}
        `
  }, [value.layersData])

  // Resize canvas
  const relativeContainerRef = React.useRef<HTMLDivElement>(null)
  const [relativeContainerWidth, setRelativeContainerWidth] = React.useState(0)
  const absoluteContainerRef = React.useRef<HTMLDivElement>(null)
  const resizeContainers = () => {
    if (absoluteContainerRef.current && relativeContainerRef.current) {
      const relativeContainerWidth = relativeContainerRef.current.offsetWidth // block width

      // Scale the absolute container so it has the full block width and preserve the aspect ratio of the OG image
      const scaleFactor = relativeContainerWidth / OG_IMAGE_WIDTH
      absoluteContainerRef.current.style.transform = `scale(${scaleFactor})`

      // Set the height of the relative container so it has the same aspect ratio as the OG image
      const relativeContainerHeight =
        relativeContainerWidth * (OG_IMAGE_HEIGHT / OG_IMAGE_WIDTH)

      relativeContainerRef.current.style.height = `${relativeContainerHeight}px`
    }
  }

  React.useEffect(() => {
    resizeContainers()

    const handleResize = (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        setRelativeContainerWidth(entry.contentRect.width)
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)

    window.addEventListener('resize', resizeContainers)
    if (relativeContainerRef.current) {
      resizeObserver.observe(relativeContainerRef.current)
    }

    return () => {
      window.removeEventListener('resize', resizeContainers)
      if (relativeContainerRef.current) {
        // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/exhaustive-deps
        resizeObserver.unobserve(relativeContainerRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    resizeContainers()
  }, [relativeContainerWidth])

  const selectedVariants = useSelectedVariants({
    parentBlock: parent,
    ignoreDefault: false,
  })

  const nodeBlock = useTreeSlice(blockTreeManager, (s) => s.getNode(parent.id))

  const getVariables: GetVariables = React.useCallback(
    (segments) => {
      if (!blockTreeManager || !repo) return []
      if (!nodeBlock) return []
      try {
        return getPossibleVariables({
          segments,
          nodeBlock,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          treeManager: blockTreeManager as any,
          valueKey: 'value',
          rootOptions: getOgBlockRootOptions({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            blockTreeManager: blockTreeManager as any,
            repo,
          }),
          selectedVariants,
        })
      } catch (error) {
        console.error(error)
        return []
      }
    },
    [blockTreeManager, repo, nodeBlock, selectedVariants]
  )

  const [_revalidate, setRevalidate] = React.useState(0)

  React.useEffect(() => {
    if (!blockTreeManager) return
    const subId = blockTreeManager.subscribe(() => {
      setRevalidate((prev) => prev + 1)
    })

    return () => {
      blockTreeManager.unsubscribe(subId)
    }
  }, [blockTreeManager])

  return (
    <div
      className="flex flex-col gap-3 focus:border-dark-control-base focus:border focus:outline-none border border-transparent"
      data-og-image-block
      tabIndex={0}
      // onBlur={(e) => {
      //   const current = e.currentTarget
      //   setTimeout(() => {
      //     if (
      //       !current.contains(document.activeElement) &&
      //       !document.activeElement?.closest(
      //         `[data-properties-panel="${sourceBlockId}"]`
      //       )
      //     ) {
      //       deselectLayer()
      //     }
      //   })
      // }}
    >
      <style>{fontsCSS}</style>

      {/* Toolbar */}
      {!isReadOnly && (
        <div className="h-6 flex justify-between">
          {/* Add Layer */}
          <div className="w-full h-full justify-start items-center flex gap-2">
            {/* Shape */}
            <Button
              size="xs"
              intent="secondary"
              className="!rounded !h-6 !px-1.5"
              onClick={async (e) => {
                e.preventDefault()
                await addLayer(newShapeLayer)
              }}
            >
              <SquareIcon width={15} height={15} />
              <span>Shape</span>
            </Button>
            {/* Image */}
            <Button
              size="xs"
              intent="secondary"
              className="!rounded !h-6 !px-1.5"
              onClick={async (e) => {
                e.preventDefault()
                await addLayer(newImageLayer)
              }}
            >
              <ImageIcon width={15} height={15} />
              <span>Image</span>
            </Button>
            {/* Text */}
            <Button
              size="xs"
              intent="secondary"
              className="!rounded !h-6 !pr-1.5 !pl-[3px]"
              onClick={async (e) => {
                e.preventDefault()
                await addLayer(newTextLayer)
              }}
            >
              <TextIcon width={16} height={16} />
              <span>Text</span>
            </Button>
          </div>
          {overriding && hasOverrides && (
            <div>
              <Button
                size="xs"
                intent="secondary"
                className="!rounded !h-6 !px-1.5"
                onClick={async (e) => {
                  e.preventDefault()
                  await reset()
                }}
              >
                <span>Reset</span>
              </Button>
            </div>
          )}
        </div>
      )}
      {/* Canvas */}
      <div
        ref={relativeContainerRef}
        className="relative w-full flex justify-center items-center rounded"
        onMouseDown={async () => {
          await deselectLayer()
        }}
        style={{
          overflow:
            typeof selectedLayerPositionZ === 'number' ? 'unset' : 'hidden',
        }}
      >
        <div
          ref={absoluteContainerRef}
          className="absolute h-[630px] w-[1200px]"
        >
          {floatingLabelTwitter && !value.showTemplates && (
            <div className="absolute bottom-8 left-8 right-8 z-50 line-clamp-1 w-fit">
              <div className="flex h-14 items-center justify-center self-start rounded-[8px] bg-black/30 px-2">
                <span className="line-clamp-1 break-words break-all text-left text-[38px] font-normal leading-8 text-dark-bright pl-1">
                  Your site's title will be rendered here in the X feed.
                </span>
              </div>
            </div>
          )}

          {value.showTemplates && !isReadOnly && editorMode === 'write:all' ? (
            // Templates
            <div className="flex h-full w-full justify-center items-center flex-col gap-12 bg-dark-shade select-none">
              <span className="text-[28px] leading-9 font-medium text-dark-bright">
                Select an Open Graph template
              </span>
              <div className="grid grid-cols-2 gap-8">
                {/* Blog Article */}
                <div
                  className="flex flex-col gap-4 justify-center items-center hover:scale-105 transition-transform duration-200"
                  onClick={async () => {
                    await addTemplate(blogArticleTemplateLayer)
                  }}
                >
                  <div className="w-[400px] h-[210px] border flex border-dark-elevated bg-dark-base-dark rounded-lg overflow-hidden">
                    <div className="w-1/2 p-4 h-full flex flex-col justify-between items-start">
                      <div
                        className="rounded-md w-16 h-8 opacity-15"
                        style={{
                          background:
                            'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                        }}
                      />
                      <div className="flex w-full h-12 flex-col">
                        <div
                          className="rounded-md w-full h-6 opacity-15"
                          style={{
                            background:
                              'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                          }}
                        />
                        <div
                          className="rounded-md w-1/2 h-4 opacity-[0.08] mt-2"
                          style={{
                            background:
                              'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                          }}
                        />
                      </div>
                      <div className="flex gap-2 w-full h-6 justify-start items-center">
                        <div
                          className="rounded-full w-8 h-8 opacity-[0.08]"
                          style={{
                            background:
                              'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                          }}
                        />
                        <div
                          className="rounded-md w-1/2 h-4 opacity-[0.08]"
                          style={{
                            background:
                              'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                          }}
                        />
                      </div>
                    </div>
                    <div className="w-1/2 h-full bg-dark-shade-hover flex justify-center items-center">
                      <ImageIcon width={64} height={64} color="#646464" />
                    </div>
                  </div>
                  <span className="font-medium text-dark-muted text-[24px] leading-8">
                    Blog article
                  </span>
                </div>
                {/* Documentation */}
                <div
                  className="flex flex-col gap-4 justify-center items-center hover:scale-105 transition-transform duration-200"
                  onClick={async () => {
                    await addTemplate(docsTemplateLayer)
                  }}
                >
                  <div className="w-[400px] h-[210px] border flex flex-col border-dark-elevated bg-dark-base-dark rounded-lg overflow-hidden">
                    <div
                      className="w-full h-12 opacity-[0.08]"
                      style={{
                        background:
                          'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                      }}
                    />
                    <div className="w-full p-4 h-full flex flex-col justify-start items-start">
                      <div
                        className="rounded-md w-full h-6 opacity-15 mt-4"
                        style={{
                          background:
                            'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                        }}
                      />
                      <div
                        className="rounded-md w-[62.5%] h-4 opacity-[0.08] mt-2"
                        style={{
                          background:
                            'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                        }}
                      />
                      <div
                        className="rounded-md w-[37.5%] h-4 opacity-[0.08] mt-2"
                        style={{
                          background:
                            'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                        }}
                      />
                      <div
                        className="rounded-md w-3/4 h-4 opacity-[0.08] mt-2"
                        style={{
                          background:
                            'linear-gradient(90deg, var(--color-bright) 0%, rgba(243, 243, 243, 0.6) 100%)',
                        }}
                      />
                    </div>
                  </div>
                  <span className="font-medium text-dark-muted text-[24px] leading-8">
                    Documentation
                  </span>
                </div>
              </div>
              <span className="text-dark-muted text-[24px] leading-8">
                or start with a{' '}
                <span
                  className="text-dark-bright underline-offset-4 hover:underline"
                  onClick={async () => {
                    await setShowTemplates(false)
                  }}
                >
                  blank canvas
                </span>
              </span>
            </div>
          ) : (
            // Layers
            <div className="flex h-full w-full">
              {/* This is replicated with satori on og generation*/}
              {value.layersData
                .slice()
                .sort((a, b) => a.positionZ - b.positionZ)
                .map((layer, i) => {
                  const isSelected =
                    layer.positionZ.toString() ===
                    selectedLayerPositionZ?.toString()

                  return (
                    <React.Fragment key={i}>
                      {layer.positionZ === 0 && layer.type === 'shape' ? (
                        // Background Layer
                        <div
                          key={`${sourceBlockId}-backgroundLayer`}
                          className="select-none appearance-none focus:outline-none"
                          style={{
                            position: 'absolute',
                            left: layer.positionX,
                            top: layer.positionY,
                            transform: `rotate(${layer.rotation}deg)`,
                            width: layer.width,
                            height: layer.height,
                            borderRadius: layer.cornerRadius,
                            opacity: layer.opacity,
                            backgroundColor: layer.color,
                          }}
                          id={`el-${sourceBlockId}-layer-bg`}
                        />
                      ) : (
                        <OgImageLayerContextMenuWrapper
                          layer={layer}
                          block={_block}
                          layerFunctions={layerFunctions}
                          path={path}
                        >
                          {layer.type === 'text' ? (
                            <TextLayer
                              key={`${sourceBlockId}-${layer.positionZ}`}
                              layer={layer}
                              handleSelectedLayerMouseDown={
                                handleSelectedLayerMouseDown
                              }
                              handleSelectedLayerKeyDown={
                                handleSelectedLayerKeyDown
                              }
                              handleCornerControlMouseDown={
                                handleCornerControlMouseDown
                              }
                              handleSideControlMouseDown={
                                handleSideControlMouseDown
                              }
                              isSelected={isSelected}
                              isDragging={isDragging}
                              editorMode={editorMode}
                              getVariables={getVariables}
                              id={`el-${sourceBlockId}-layer-${i}`}
                            />
                          ) : layer.type === 'image' ? (
                            <ImageLayer
                              key={`${sourceBlockId}-${layer.positionZ}`}
                              layer={layer}
                              handleSelectedLayerMouseDown={
                                handleSelectedLayerMouseDown
                              }
                              handleSelectedLayerKeyDown={
                                handleSelectedLayerKeyDown
                              }
                              handleCornerControlMouseDown={
                                handleCornerControlMouseDown
                              }
                              handleSideControlMouseDown={
                                handleSideControlMouseDown
                              }
                              isSelected={isSelected}
                              isDragging={isDragging}
                              editorMode={editorMode}
                              getVariables={getVariables}
                            />
                          ) : (
                            <ShapeLayer
                              key={`${sourceBlockId}-${layer.positionZ}`}
                              layer={layer}
                              handleSelectedLayerMouseDown={
                                handleSelectedLayerMouseDown
                              }
                              handleSelectedLayerKeyDown={
                                handleSelectedLayerKeyDown
                              }
                              handleCornerControlMouseDown={
                                handleCornerControlMouseDown
                              }
                              handleSideControlMouseDown={
                                handleSideControlMouseDown
                              }
                              isSelected={isSelected}
                              isDragging={isDragging}
                              editorMode={editorMode}
                              id={`el-${sourceBlockId}-layer-${i}`}
                            />
                          )}
                        </OgImageLayerContextMenuWrapper>
                      )}
                    </React.Fragment>
                  )
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const TextLayer = ({
  layer,
  handleSelectedLayerMouseDown = async (_) => {},
  handleSelectedLayerKeyDown = async (_) => {},
  handleCornerControlMouseDown = async (_) => {},
  handleSideControlMouseDown = async (_) => {},
  isSelected,
  isDragging,
  editorMode,
  getVariables,
  id,
}: {
  layer: OgImageBlockTextLayerType
  handleSelectedLayerMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType
  ) => Promise<void>
  handleSelectedLayerKeyDown?: (
    e: React.KeyboardEvent<HTMLButtonElement>,
    layer: OgImageBlockLayerType
  ) => Promise<void>
  handleCornerControlMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 'tl' | 'tr' | 'bl' | 'br'
  ) => Promise<void>
  handleSideControlMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 't' | 'r' | 'b' | 'l'
  ) => Promise<void>
  isSelected?: boolean
  isDragging?: boolean
  editorMode?: string
  getVariables: GetVariables
  id: string
}) => {
  const textLayerRef = React.useRef<HTMLButtonElement>(null)
  const prevFontFamilyRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (isSelected) {
      textLayerRef.current?.focus()
    }
  }, [isSelected])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      prevFontFamilyRef.current = layer.fontFamily
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [layer.fontFamily])

  return (
    <button
      key={layer.positionZ + layer.type}
      ref={textLayerRef}
      onKeyDown={async (e) => {
        await handleSelectedLayerKeyDown(e, layer)
      }}
      onMouseDown={(e) => {
        handleSelectedLayerMouseDown(e, layer)
      }}
      className={`${
        !isSelected && editorMode === 'write:all' && !isDragging
          ? 'hover:ring-[3px] ring-dark-control-base'
          : ''
      } select-none appearance-none focus:outline-none cursor-default w-full h-full`}
      style={{
        position: 'absolute',
        display: 'flex',
        justifyContent:
          layer.alignHorizontal === 'center'
            ? 'center'
            : layer.alignHorizontal === 'right'
              ? 'flex-end'
              : 'flex-start',
        alignItems:
          layer.alignVertical === 'middle'
            ? 'center'
            : layer.alignVertical === 'bottom'
              ? 'flex-end'
              : 'flex-start',
        left: layer.positionX,
        top: layer.positionY,
        width: layer.width,
        height: layer.height,
        transform: `rotate(${layer.rotation}deg)`,
      }}
      data-ignore-editor-keydown
    >
      <div
        className="line-clamp-1"
        style={{
          overflowWrap: 'break-word',
          opacity: layer.opacity,
          fontFamily: `${layer.fontFamily}, ${
            // eslint-disable-next-line react-compiler/react-compiler
            prevFontFamilyRef.current ? prevFontFamilyRef.current : 'sans-serif'
          }`,
          fontWeight: layer.fontWeight,
          fontSize: layer.size,
          lineHeight: `${layer.lineHeight}px`,
          lineClamp: layer.lineClamp,
          WebkitLineClamp: layer.lineClamp,
          textAlign: layer.alignHorizontal,
          verticalAlign: layer.alignVertical,
          color: layer.color,
        }}
        id={id}
      >
        {resolveInputWithVars(layer.textValue, {
          getPreviewUrl: () => '',
          getVariables,
        }) || <span className="opacity-30">{layer.textValue}</span>}
      </div>

      {isSelected && (
        <>
          <CornerControls
            layer={layer}
            handleCornerControlMouseDown={handleCornerControlMouseDown}
          />
          <SideControls
            layer={layer}
            handleSideControlMouseDown={handleSideControlMouseDown}
          />
        </>
      )}
    </button>
  )
}

export const ImageLayer = ({
  layer,
  handleSelectedLayerMouseDown = async () => {},
  handleSelectedLayerKeyDown = async () => {},
  handleCornerControlMouseDown = async () => {},
  handleSideControlMouseDown = async () => {},
  isSelected,
  isDragging,
  editorMode,
  getVariables,
}: {
  layer: OgImageBlockImageLayerType
  handleSelectedLayerMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType
  ) => Promise<void>
  handleSelectedLayerKeyDown?: (
    e: React.KeyboardEvent<HTMLButtonElement>,
    layer: OgImageBlockLayerType
  ) => Promise<void>
  handleCornerControlMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 'tl' | 'tr' | 'bl' | 'br'
  ) => Promise<void>
  handleSideControlMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 't' | 'r' | 'b' | 'l'
  ) => Promise<void>
  isSelected?: boolean
  isDragging?: boolean
  editorMode?: string
  getVariables: GetVariables
}) => {
  const uploadedImageLayerRef = React.useRef<HTMLButtonElement>(null)
  const emptyImageLayerRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (isSelected) {
      emptyImageLayerRef.current?.focus()
      uploadedImageLayerRef.current?.focus()
    }
  }, [isSelected])
  const inputWithVars =
    layer.imageValue.type === 'dynamic'
      ? resolveInputWithVars(layer.imageValue.value, {
          getPreviewUrl: () => '',
          getVariables,
        })
      : layer.imageValue.value

  const isSvg =
    layer.imageValue.type === 'dynamic' && typeof inputWithVars === 'string'
      ? inputWithVars.includes('<svg')
      : false

  return (
    <>
      {(layer.imageValue &&
        layer.imageValue.type === 'dynamic' &&
        layer.imageValue.value &&
        resolveInputWithVars(layer.imageValue.value, {
          getPreviewUrl: () => '',
          getVariables,
        })) ||
      (layer.imageValue.type === 'static' &&
        layer.imageValue.value.status === 'uploaded') ? (
        <button
          key={layer.positionZ + layer.type}
          ref={uploadedImageLayerRef}
          onKeyDown={async (e) => {
            await handleSelectedLayerKeyDown(e, layer)
          }}
          className={`${
            !isSelected && editorMode === 'write:all' && !isDragging
              ? 'hover:ring-[3px] ring-dark-control-base'
              : ''
          } select-none appearance-none focus:outline-none cursor-default`}
          style={{
            position: 'absolute',
            left: layer.positionX,
            top: layer.positionY,
            width: layer.width,
            height: layer.height,
            transform: `rotate(${layer.rotation}deg)`,
          }}
          data-ignore-editor-keydown
        >
          {isSvg && typeof inputWithVars === 'string' ? (
            <Icon
              content={inputWithVars}
              components={{
                svg: (props) => (
                  <svg
                    width={layer.width}
                    height={layer.height}
                    className="select-none"
                    onMouseDown={(e) => {
                      handleSelectedLayerMouseDown(
                        e as unknown as React.MouseEvent<HTMLButtonElement>,
                        layer
                      )
                    }}
                    style={{
                      width: layer.width,
                      height: layer.height,
                      borderRadius: layer.cornerRadius,
                      opacity: layer.opacity,
                      objectFit: layer.objectFit,
                      objectPosition: 'center',
                    }}
                    {...props}
                  />
                ),
              }}
            />
          ) : (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={
                  layer.imageValue.type === 'static' &&
                  layer.imageValue.value.status === 'uploaded'
                    ? layer.imageValue.value.url
                    : layer.imageValue.type === 'dynamic'
                      ? (inputWithVars as ReturnType<
                          typeof resolveInputWithVars
                        >)
                      : ''
                }
                width={layer.width}
                height={layer.height}
                alt=""
                className="select-none"
                onMouseDown={(e) => {
                  handleSelectedLayerMouseDown(e, layer)
                }}
                style={{
                  width: layer.width,
                  height: layer.height,
                  borderRadius: layer.cornerRadius,
                  opacity: layer.opacity,
                  objectFit: layer.objectFit,
                  objectPosition: 'center',
                }}
              />
            </>
          )}
          {isSelected && (
            <>
              <CornerControls
                layer={layer}
                handleCornerControlMouseDown={handleCornerControlMouseDown}
              />
              <SideControls
                layer={layer}
                handleSideControlMouseDown={handleSideControlMouseDown}
              />
            </>
          )}
        </button>
      ) : (
        <button
          key={layer.positionZ + layer.type}
          ref={emptyImageLayerRef}
          onKeyDown={async (e) => {
            await handleSelectedLayerKeyDown(e, layer)
          }}
          className={`${
            !isSelected && editorMode === 'write:all' && !isDragging
              ? 'hover:ring-[3px] ring-dark-control-base'
              : ''
          } select-none appearance-none focus:outline-none cursor-default flex justify-center items-center `}
          style={{
            position: 'absolute',
            left: layer.positionX,
            top: layer.positionY,
            transform: `rotate(${layer.rotation}deg)`,
            width: layer.width,
            height: layer.height,
            opacity: layer.opacity,
          }}
          data-ignore-editor-keydown
        >
          <div
            onMouseDown={(e) => {
              handleSelectedLayerMouseDown(e, layer)
            }}
            className={`${
              layer.imageValue.type === 'dynamic' && layer.imageValue.value
                ? ''
                : 'bg-black'
            } flex justify-center items-center border-dashed border-dark-border-solid border-4`}
            style={{
              width: layer.width,
              height: layer.height,
              borderRadius: layer.cornerRadius,
            }}
          >
            <ImageIcon
              width={layer.width < 100 || layer.height < 100 ? 32 : 64}
              height={layer.width < 100 || layer.height < 100 ? 32 : 64}
              color="var(--color-border-solid)"
            />
          </div>

          {isSelected && (
            <>
              <CornerControls
                layer={layer}
                handleCornerControlMouseDown={handleCornerControlMouseDown}
              />
              <SideControls
                layer={layer}
                handleSideControlMouseDown={handleSideControlMouseDown}
              />
            </>
          )}
        </button>
      )}
    </>
  )
}

export const ShapeLayer = ({
  layer,
  handleSelectedLayerMouseDown = async (_) => {},
  handleSelectedLayerKeyDown = async (_) => {},
  handleCornerControlMouseDown = async (_) => {},
  handleSideControlMouseDown = async (_) => {},
  isSelected,
  isDragging,
  editorMode,
  id,
}: {
  layer: OgImageBlockShapeLayerType
  handleSelectedLayerMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType
  ) => Promise<void>
  handleSelectedLayerKeyDown?: (
    e: React.KeyboardEvent<HTMLButtonElement>,
    layer: OgImageBlockLayerType
  ) => Promise<void>
  handleCornerControlMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 'tl' | 'tr' | 'bl' | 'br'
  ) => Promise<void>
  handleSideControlMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 't' | 'r' | 'b' | 'l'
  ) => Promise<void>
  isSelected?: boolean
  isDragging?: boolean
  editorMode?: string
  id: string
}) => {
  const shapeLayerRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (isSelected) {
      shapeLayerRef.current?.focus()
    }
  }, [isSelected])

  return (
    <button
      key={layer.positionZ + layer.type}
      ref={shapeLayerRef}
      onKeyDown={async (e) => {
        await handleSelectedLayerKeyDown(e, layer)
      }}
      className={`${
        !isSelected && editorMode === 'write:all' && !isDragging
          ? 'hover:ring-[3px] ring-dark-control-base'
          : ''
      } select-none appearance-none focus:outline-none cursor-default`}
      style={{
        position: 'absolute',
        left: layer.positionX,
        top: layer.positionY,
        transform: `rotate(${layer.rotation}deg)`,
        width: layer.width,
        height: layer.height,
        opacity: layer.opacity,
      }}
      data-ignore-editor-keydown
    >
      <div
        onMouseDown={(e) => {
          handleSelectedLayerMouseDown(e, layer)
        }}
        style={{
          width: layer.width,
          height: layer.height,
          borderRadius: layer.cornerRadius,
          backgroundColor: layer.color,
        }}
        id={id}
      />

      {isSelected && (
        <>
          <CornerControls
            layer={layer}
            handleCornerControlMouseDown={handleCornerControlMouseDown}
          />
          <SideControls
            layer={layer}
            handleSideControlMouseDown={handleSideControlMouseDown}
          />
        </>
      )}
    </button>
  )
}

const CornerControls = ({
  layer,
  handleCornerControlMouseDown,
}: {
  layer: OgImageBlockLayerType
  handleCornerControlMouseDown: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 'tl' | 'tr' | 'bl' | 'br'
  ) => Promise<void>
}) => {
  return (
    <>
      <div
        onMouseDown={(e) => handleCornerControlMouseDown(e, layer, 'tl')}
        className="absolute border-dark-control-base border-2 bg-dark-bg"
        style={{
          width: 12,
          height: 12,
          cursor: `nwse-resize`,
          top: -7,
          left: -7,
        }}
      />
      <div
        onMouseDown={(e) => handleCornerControlMouseDown(e, layer, 'tr')}
        className="absolute border-dark-control-base border-2 bg-dark-bg"
        style={{
          width: 12,
          height: 12,
          cursor: `nesw-resize`,
          top: -7,
          right: -7,
        }}
      />
      <div
        onMouseDown={(e) => handleCornerControlMouseDown(e, layer, 'bl')}
        className="absolute border-dark-control-base border-2 bg-dark-bg"
        style={{
          width: 12,
          height: 12,
          cursor: 'nesw-resize',
          bottom: -7,
          left: -7,
        }}
      />
      <div
        onMouseDown={(e) => handleCornerControlMouseDown(e, layer, 'br')}
        className="absolute border-dark-control-base border-2 bg-dark-bg"
        style={{
          width: 12,
          height: 12,
          cursor: `nwse-resize`,
          bottom: -7,
          right: -7,
        }}
      />
    </>
  )
}

const SideControls = ({
  layer,
  handleSideControlMouseDown,
}: {
  layer: OgImageBlockLayerType
  handleSideControlMouseDown: (
    e: React.MouseEvent<HTMLButtonElement | HTMLElement>,
    layer: OgImageBlockLayerType,
    control: 't' | 'r' | 'b' | 'l'
  ) => Promise<void>
}) => {
  return (
    <>
      <div
        onMouseDown={(e) => handleSideControlMouseDown(e, layer, 'l')}
        className="absolute bg-dark-control-base"
        style={{
          top: 5,
          left: -2,
          width: 2,
          height: 'calc(100% - 10px)',
          cursor: 'ew-resize',
        }}
      />
      <div
        onMouseDown={(e) => handleSideControlMouseDown(e, layer, 'r')}
        className="absolute bg-dark-control-base"
        style={{
          top: 5,
          right: -2,
          width: 2,
          height: 'calc(100% - 10px)',
          cursor: 'ew-resize',
        }}
      />
      <div
        onMouseDown={(e) => handleSideControlMouseDown(e, layer, 't')}
        className="absolute bg-dark-control-base"
        style={{
          top: -2,
          left: 5,
          width: 'calc(100% - 10px)',
          height: 2,
          cursor: 'ns-resize',
        }}
      />
      <div
        onMouseDown={(e) => handleSideControlMouseDown(e, layer, 'b')}
        className="absolute bg-dark-control-base"
        style={{
          bottom: -2,
          left: 5,
          width: 'calc(100% - 10px)',
          height: 2,
          cursor: 'ns-resize',
        }}
      />
    </>
  )
}
